import { useEffect, useState } from "react";
import { ArrowRight, MapPin } from "lucide-react";
import Seo from "@/components/Seo";
import { STATES } from "@/data/states";
import { trackEvent } from "@/lib/analytics";
import { SubpageShell } from "@/components/landing/SubpageShell";
import { CheckoutModal } from "@/components/landing/CheckoutModal";
import { Reveal } from "@/components/landing/Reveal";

export default function CoverageIndex() {
    const [checkoutOpen, setCheckoutOpen] = useState(false);

    useEffect(() => {
        trackEvent("page_view");
    }, []);

    return (
        <SubpageShell onQuote={() => setCheckoutOpen(true)}>
            <Seo
                title="Extended Car Warranty by State — All 50 States | AdvantaShield"
                siteName="AdvantaShield"
                description="AdvantaShield vehicle service contracts are available in all 50 states. Dealer-built extended car warranty coverage with subscription pricing — find your state."
                image="/og-image.png"
                jsonLd={{
                    "@context": "https://schema.org",
                    "@type": "ItemList",
                    name: "AdvantaShield coverage by state",
                    numberOfItems: STATES.length,
                    itemListElement: STATES.map((s, i) => ({
                        "@type": "ListItem",
                        position: i + 1,
                        name: `Extended car warranty in ${s.name}`,
                        url: `${window.location.origin}/coverage/${s.slug}`,
                    })),
                }}
            />

            <Reveal>
                <p className="font-mono text-xs font-semibold uppercase tracking-[0.3em] text-sky-400">
                    Coverage by State
                </p>
                <h1 className="mt-5 font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl">
                    Extended car warranty coverage,{" "}
                    <span className="text-sky-400">coast to coast.</span>
                </h1>
                <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-400 md:text-lg">
                    AdvantaShield vehicle service contracts are available in all 50
                    states — built by dealership service directors, accepted at the
                    service counter, and priced as a monthly subscription you can
                    cancel anytime. Find your state below.
                </p>
            </Reveal>

            <Reveal delay={0.1}>
                <div
                    data-testid="coverage-state-grid"
                    className="mt-12 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3"
                >
                    {STATES.map((s) => (
                        <a
                            key={s.slug}
                            href={`/coverage/${s.slug}`}
                            data-testid={`coverage-link-${s.slug}`}
                            className="group flex items-center justify-between rounded-2xl border border-white/10 bg-[#121722] px-5 py-4 transition-[border-color,transform] duration-300 hover:-translate-y-0.5 hover:border-sky-400/40"
                        >
                            <span className="flex items-center gap-3">
                                <MapPin className="h-4 w-4 text-sky-400" />
                                <span>
                                    <span className="block text-sm font-semibold text-white">
                                        {s.name}
                                    </span>
                                    <span className="block text-xs text-slate-500">
                                        {s.cities.join(" · ")}
                                    </span>
                                </span>
                            </span>
                            <ArrowRight className="h-4 w-4 text-slate-600 transition-all duration-300 group-hover:translate-x-1 group-hover:text-sky-400" />
                        </a>
                    ))}
                </div>
            </Reveal>

            <Reveal delay={0.1}>
                <div className="mt-14 rounded-3xl border border-sky-400/20 bg-[#0C1019] p-8 md:p-12">
                    <h2 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">
                        Not sure where to start?{" "}
                        <span className="text-sky-400">Get a rate for your vehicle.</span>
                    </h2>
                    <p className="mt-4 max-w-xl text-base text-slate-400">
                        Quotes take under a minute and are built on your vehicle's
                        year, make, model, and mileage — no robocalls, ever.
                    </p>
                    <button
                        data-testid="coverage-quote-btn"
                        onClick={() => setCheckoutOpen(true)}
                        className="mt-7 inline-flex items-center gap-2.5 rounded-full bg-sky-400 px-7 py-4 text-base font-bold text-[#0A0D14] transition-[background-color,box-shadow,transform] duration-300 hover:bg-sky-300 hover:shadow-[0_0_44px_rgba(56,189,248,0.5)] active:scale-95"
                    >
                        Get My Instant Quote
                        <ArrowRight className="h-5 w-5" />
                    </button>
                </div>
            </Reveal>

            <CheckoutModal open={checkoutOpen} onOpenChange={setCheckoutOpen} />
        </SubpageShell>
    );
}
